import { useEffect } from 'react';

import { playSound } from './soundService';
import { SoundId } from './soundConstants';

/** Semantic class the Roma design-system Checkbox puts on its root (see @roma Checkbox.tsx) */
const ROMA_CHECKBOX_SELECTOR = '.RomaCheckbox';

/** A label click can be followed by a synthesised click on its input — treat those as one */
const DUPLICATE_CLICK_WINDOW_MS = 100;

/**
 * Plays tick / untick sounds for the Roma design-system checkbox.
 *
 * Roma lives in `src/common/@roma` and can't depend on client code, and each consumer wires
 * its own `onChange`, so instead of hooking every call site we listen at the document level.
 * react-aria (which Roma's Checkbox wraps) handles the click itself and never dispatches a
 * native `change` event, so we listen for the `click` in the capture phase and read the
 * input's state *before* it toggles. (The presentational `Checkbox.Base` — used e.g. by task
 * list items — has no input, so it never fires here.)
 */
export const useRomaCheckboxSounds = (enabled: boolean) => {
    useEffect(() => {
        if (!enabled || typeof document === 'undefined') return undefined;

        let lastRoot: Element | null = null;
        let lastClickTime = 0;

        const onClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const root = target.closest(ROMA_CHECKBOX_SELECTOR);
            if (!root) return;

            const input = root.querySelector<HTMLInputElement>('input[type="checkbox"]');
            if (!input || input.disabled || input.readOnly) return;

            const now = Date.now();
            if (root === lastRoot && now - lastClickTime < DUPLICATE_CLICK_WINDOW_MS) return;
            lastRoot = root;
            lastClickTime = now;

            // Capture phase: `checked` is still the pre-toggle state
            playSound(input.checked ? SoundId.CheckboxUntick : SoundId.CheckboxTick);
        };

        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, [enabled]);
};
